import type {
  ExecuteContractActionMutationOptions,
  ExecuteContractActionMutationPayload,
} from './types';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { queryKeysContract } from '../queryKeys';

import { executeContractAction } from './executeContractAction';

/**
 * Mutation key for contract action execution.
 * Used with useMutationState to track pending contract actions.
 */
export const MUTATION_KEY_EXECUTE_CONTRACT_ACTION = [
  'contracts',
  'executeAction',
] as const;

/**
 * Custom hook for executing an action on a contract (status transition).
 * Available actions: confirm, start, complete, cancel, delete.
 *
 * @param options - Additional options for the mutation.
 * @returns An object containing the mutation function and related properties.
 * @see https://app.dev.vestigas.com/redoc#tag/Contracts/operation/execute_contract_action_contracts__contract_id__action_post
 */
export const useMutationExecuteContractAction = (
  options?: ExecuteContractActionMutationOptions,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      contractId,
      ...payload
    }: ExecuteContractActionMutationPayload) =>
      executeContractAction(contractId, payload),
    mutationKey: MUTATION_KEY_EXECUTE_CONTRACT_ACTION,
    onSuccess(_data, { contractId }) {
      // Invalidate the single contract query
      queryClient.invalidateQueries({
        queryKey: queryKeysContract.get(contractId),
      });

      // Invalidate the contracts list (status changed)
      queryClient.invalidateQueries({
        queryKey: queryKeysContract.getAll(undefined),
      });

      // Invalidate the contract actions (available actions may have changed)
      queryClient.invalidateQueries({
        queryKey: queryKeysContract.getActions(contractId),
      });

      // Invalidate the contract permissions (permissions may have changed with status)
      queryClient.invalidateQueries({
        queryKey: queryKeysContract.getPermissions(contractId),
      });

      // Invalidate the public view
      queryClient.invalidateQueries({
        queryKey: queryKeysContract.getPublicView(contractId),
      });

      // Invalidate the contract history (new history entry added)
      queryClient.invalidateQueries({
        queryKey: queryKeysContract.getHistory(contractId),
      });
    },
    ...options,
  });
};
